import { useContext, useEffect, useRef, useState } from 'react';

import { Input, Button, GlobalContext, Alert, Dialog } from 'lynkco-up-core';
import { ReactInputEventHandler } from 'lynkco-up-core/dist/types/types';

import { Divider, VerificationScopeSelect } from '../../../components';
import { InputWrapper } from '../../shared';
import { ApplicantQuery, VerificationScope, VerificationType } from '../../../types';
import { getUserData } from '../../../services/GDPR';
import Results from './Results';
import { UserData } from '../../../services/GDPR/types';
import { buildOnfidoUrl } from './utils';
import { getUserSafeServiceError, isNoDataError } from '../../../utils/serviceError';
import { toVerificationTypeHeader } from '../../../utils/verificationScope';

const getMaxLengthErrorMessage = (limit: number) => `This value cannot exceed ${limit} characters.`;
const applicationProgressErrorMessage =
  'We could not load verification progress with the details provided. Check the input and try again.';
const noVerificationDataMessage = 'No verification data is available for the provided details.';

function ApplicationProgressContent({ applicantQuery }: { applicantQuery: ApplicantQuery }) {
  const { alert, loading } = useContext(GlobalContext);
  const [customerNumber, setCustomerNumber] = useState(applicantQuery.customerNumber);
  const [isCustomerSuggested, setIsCustomerSuggested] = useState(applicantQuery.customerNumber);
  const [userData, setUserData] = useState<null | UserData>(null);
  const [verificationScope, setVerificationScope] = useState<VerificationScope>(VerificationType.CAR_SHARING);
  const [submittedVerificationScope, setSubmittedVerificationScope] = useState<VerificationScope | null>(null);
  const [submittedCustomerNumber, setSubmittedCustomerNumber] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState({
    customerNumber: false,
  });
  const [resultError, setResultError] = useState<string | null>(null);
  const [noDataMessage, setNoDataMessage] = useState<string | null>(null);
  const isSubmitDisabled = !customerNumber || Object.values(formErrors).some(error => error);
  const inputRef = useRef<null | HTMLInputElement>(null);

  const [isRedirectToOnfidoDialogOpen, setIsRedirectToOnfidoDialogOpen] = useState(false);

  const handleChangeCustomerNumber: ReactInputEventHandler = e => {
    setCustomerNumber(e.currentTarget.value);
  };

  function handleDismissResultError() {
    setResultError(null);
  }

  function handleDismissCustomerSuggestion() {
    setIsCustomerSuggested('');
  }

  function handleOpenRedirectToOnfidoDialog() {
    setIsRedirectToOnfidoDialogOpen(true);
  }

  function handleCloseRedirectToOnfidoDialog() {
    setIsRedirectToOnfidoDialogOpen(false);
  }

  function handleRedirectToOnfido() {
    const url = buildOnfidoUrl(userData?.onfidoApplicant.id);

    handleCloseRedirectToOnfidoDialog();
    window.open(url, '_blank');
  }

  async function handleSubmit() {
    if (isSubmitDisabled) {
      return;
    }

    if (resultError) {
      setResultError(null);
    }
    if (noDataMessage) {
      setNoDataMessage(null);
    }

    loading.show();

    try {
      const requestedVerificationScope = verificationScope;
      const data = await getUserData('0000', customerNumber, toVerificationTypeHeader(requestedVerificationScope));
      setUserData(data);
      setSubmittedVerificationScope(requestedVerificationScope);
      setSubmittedCustomerNumber(customerNumber);
      alert.show('Data successfully retrieved.', 'success');
    } catch (error) {
      setUserData(null);
      if (isNoDataError(error)) {
        setNoDataMessage(noVerificationDataMessage);
      } else {
        setResultError(getUserSafeServiceError(error, applicationProgressErrorMessage));
      }
    }

    loading.hide();
  }

  useEffect(() => {
    setFormErrors({
      customerNumber: customerNumber.length > 50,
    });
  }, [customerNumber]);

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, []);

  return (
    <div>
      <h2 className="font-medium text-xl mb-7">User application progress</h2>
      {resultError && (
        <Alert
          type="error"
          heading="There was an error while trying to get user data"
          body={`${resultError}`}
          onDismiss={handleDismissResultError}
        />
      )}
      {isCustomerSuggested && (
        <Alert
          type="success"
          heading="Suggested customer"
          body={`We selected customer ${applicantQuery.customerNumber} for you`}
          onDismiss={handleDismissCustomerSuggestion}
        />
      )}
      <Divider />
      <InputWrapper label="Customer number*">
        <Input
          ref={inputRef}
          type="text"
          extraClasses="basis-2/3 -mt-1"
          onChange={handleChangeCustomerNumber}
          errorText={getMaxLengthErrorMessage(50)}
          hasError={formErrors.customerNumber}
          value={customerNumber}
        />
      </InputWrapper>
      <Divider />
      <InputWrapper label="Verification data">
        <VerificationScopeSelect value={verificationScope} onChange={setVerificationScope} />
      </InputWrapper>
      <Divider />
      <div className="flex justify-end mt-7">
        <Button onClick={() => handleSubmit()} isDisabled={isSubmitDisabled}>
          Submit
        </Button>
      </div>
      {noDataMessage && (
        <div className="mt-8 rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          {noDataMessage}
        </div>
      )}
      {userData && (
        <Results
          data={userData}
          verificationScope={submittedVerificationScope ?? verificationScope}
          selectedVerificationScope={verificationScope}
          isStale={submittedVerificationScope !== verificationScope || submittedCustomerNumber !== customerNumber}
          openRedirectToOnfido={handleOpenRedirectToOnfidoDialog}
        />
      )}
      <Dialog
        icon="info"
        isOpen={isRedirectToOnfidoDialogOpen}
        title="Redirecting to Onfido"
        onCancel={handleCloseRedirectToOnfidoDialog}
        onConfirm={handleRedirectToOnfido}
        onClose={handleCloseRedirectToOnfidoDialog}
        cancelText="Cancel"
        confirmText="Proceed">
        We will open a new tab taking you to the onfido dashboard.
      </Dialog>
    </div>
  );
}

export default ApplicationProgressContent;
